<script setup lang="ts">
import { ref } from "vue";
import { RfpLocation } from "../types/Marketplace";
import LocationTile from "@/components/LocationTile.vue";
import MapCanvas from "@/components/MapCanvas.vue";
import { aggValue } from "../plugins/Formatting.js";
import { useTheme } from "../lib/useTheme";
import { useElementVisibility } from "@vueuse/core";

const target = ref<HTMLElement | null>(null);
const targetIsVisible = useElementVisibility(target);

const { dark } = useTheme();

defineProps<{
  rfpId: string;
  location: RfpLocation;
}>();
</script>

<template>
  <location-tile ref="target">
    <template #map>
      <map-canvas
        class="map tw:rounded-md"
        :geojson="location.data.geometry"
        :fill="dark ? '#333' : '#eee'"
        fill-opacity="0.7"
        :stroke="dark ? '#888' : '#ccc'"
        stroke-width="1"
        :padding="[5, 5]"
        v-if="targetIsVisible"
      />
    </template>
    <template #title>
      <h2 v-if="location.address.formatted">
        {{ location.address.formatted }}
      </h2>
      <h2 v-else class="tw:text-zinc-400">-</h2>
    </template>
    <template #content>
      <t-chip dense v-if="location.data.utilityName" color="gray">
        <t-icon small left class="tw:text-xs">power</t-icon>
        {{ location.data.utilityName }}
      </t-chip>
      <t-chip dense v-if="location.data.roofAreaSqft" color="gray">
        <t-icon small left class="tw:text-xs">house</t-icon>
        <span v-html="aggValue(location.data.roofAreaSqft, 'sqft')" />
      </t-chip>
      <t-chip
        dense
        v-else-if="location.data.availableParcelAreaSqft"
        color="gray"
      >
        <t-icon small left class="tw:text-xs">grid_on</t-icon>
        <span
          v-html="aggValue(location.data.availableParcelAreaSqft, 'sqft')"
        />
      </t-chip>
      <t-chip dense v-if="location.data.costOfElectricity" color="gray">
        <t-icon small left class="tw:text-xs">monetization_on</t-icon>
        <span
          v-html="aggValue(location.data.costOfElectricity.toFixed(2), '/kWh')"
        />
      </t-chip>
    </template>
    <template #action>
      <TBtn
        class="tw:mb-2 tw:mr-2"
        :to="{
          name: 'rfp-location',
          params: { rfpId: rfpId, locationId: location.data.id },
        }"
      >
        view
        <t-icon right>arrow_forward</t-icon>
      </TBtn>
    </template>
  </location-tile>
</template>
